import {useCallback, useEffect, useMemo, useState} from 'react';
import {yupResolver} from '@hookform/resolvers/yup';
import {useForm, Resolver} from 'react-hook-form';
import {useNavigate} from 'react-router';
import {
  AdminSettingsFormData,
  AdminEmailData,
  AdminReviewerData,
} from './admin-settings-interface';
import {
  adminSettingsValidator,
  adminEmailValidator,
  adminReviewerValidator,
} from './admin-settings-validator';
import {AdminSettingsController} from './admin-settings-controller';
import {AdminSettingsRequestImpl} from './admin-settings-request';
import {useStoreContext} from '../../../../../../../../store/store-context';
import {useParams, useSearchParams} from 'react-router-dom';
import {generateRandomDigits} from '@utils/generate-random-numbers';
import {resizeFile} from '../../../../../../../../utils/compress-images';
import {UserAPIRequestImpl} from '../../../../../../../../api/user-api/user-api-request';
import {UserAPIController} from '../../../../../../../../api/user-api/user-api-controller';
import {groupRequestImpl} from '../../../admin-group/admin-group-request';
import {groupController} from '../../../admin-group/admin-group-controller';
import {activateNotification} from '../../../../../../../../ui/molecules/notification/activate-notification';
import axios from 'axios';
import {BaseURL} from '../../../../../../../../configs/request';
import {AnalyticsController} from '../../../../../../reporting/reporting-pages/admin-analytics/admin-analytics.controller';
import {AnalyticsRequestImpl} from '../../../../../../reporting/reporting-pages/admin-analytics/admin-analytics.request';
import {setFirebaseData} from '@utils/firebase-handler';
import {
  normalizeTimezoneIdentifier,
  serializeTimezoneForApi,
} from '@utils/timezone';

const defaultReviewerProfile = {
  firstName: '',
  lastName: '',
  id: '',
  timezone: '',
  email: '',
  avatar: {
    url: '',
  },
  role: '',
  jobTitle: '',
};

const defaultAdminProfile = {
  firstName: '',
  lastName: '',
  timezone: '',
  email: '',
  avatar: {
    url: '',
  },
  role: '',
  jobTitle: '',
  department: '',
  reviewer: defaultReviewerProfile,
  reporting: {
    schedule: '',
    weekDay: '',
  },
  phoneNumber: '',
  id: '',
  countryCode: '',
};

export function useAdminSettingsHook() {
  const [searchParams] = useSearchParams();
  const {
    register,
    handleSubmit,
    errors,
    formState,
    setValue,
    control,
    watch,
  } = useForm<AdminSettingsFormData>({
    resolver: yupResolver(adminSettingsValidator) as Resolver<
      AdminSettingsFormData,
      object
    >,
    mode: 'onSubmit',
  });

  const {
    register: emailRegister,
    control: emailControl,
    errors: emailErrors,
    formState: emailFormState,
    handleSubmit: emailHandleSubmit,
    watch: watchEmail,
  } = useForm<AdminEmailData>({
    resolver: yupResolver(adminEmailValidator) as Resolver<
      AdminEmailData,
      object
    >,
    mode: 'onSubmit',
  });

  const {
    register: reviewerRegister,
    errors: reviewerErrors,
    control: reviewerControl,
    formState: reviewerFormState,
    handleSubmit: reviewerHandleSubmit,
    watch: watchReviewer,
    setValue: setValueReviewer,
  } = useForm<AdminReviewerData>({
    resolver: yupResolver(adminReviewerValidator) as Resolver<
      AdminReviewerData,
      object
    >,
    mode: 'onSubmit',
  });

  const isSubmitting = useMemo(() => {
    return formState.isSubmitting;
  }, [formState]);

  const isSubmittingEmail = useMemo(() => {
    return emailFormState.isSubmitting;
  }, [emailFormState]);

  const isSubmittingReviewer = useMemo(() => {
    return reviewerFormState.isSubmitting;
  }, [reviewerFormState]);

  const hasError = useMemo(() => {
    return formState.submitCount > 0 && formState.errors;
  }, [formState.errors, formState.submitCount]);

  const handleSelectChange = (value: string) => {
    setValue('timezone', value);
  };

  const handleSelectChangeGroups = (data: {value: string; label: string}[]) => {
    setValue('groups', data);
  };

  const handleSelectChangeReviewer = (value: string) => {
    setValueReviewer('reviewer', value);
  };

  const handleSelectChangeWeekday = (weekDay: string) => {
    setValueReviewer('reporting.weekDay', weekDay);
  };
  const handleSelectChangeSchedule = (schedule: string) => {
    setValueReviewer('reporting.schedule', schedule);
  };
  const handleSetPrefix = (prefix: string) => {
    setValue('countryCode', prefix);
  };

  const {
    usersStore,
    authStore: {setInitResponseAsAuth, auth},
    analyticsStore: {setFilterGroup},
  } = useStoreContext();
  //
  let {id: useParamsId} = useParams();
  const currentPage = searchParams.get('page') || '1';
  const membersPageRoute = `/manage-people?page=${currentPage}`;

  const [profile, setProfiles] = useState(defaultAdminProfile);

  let profileAvatar = profile.avatar ? profile.avatar.url : '';

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${BaseURL}/users/${useParamsId}?include=[reviewer]`, {
        headers: {
          Authorization: `Bearer ${auth?.tokens.access.token}`,
        },
      })
      .then((response) => {
        let userData = response.data;
        setProfiles({
          ...defaultAdminProfile,
          ...userData,
          avatar: {
            ...defaultAdminProfile.avatar,
            ...(userData?.avatar || {}),
          },
          reviewer: {
            ...defaultReviewerProfile,
            ...(userData?.reviewer || {}),
            avatar: {
              ...defaultReviewerProfile.avatar,
              ...(userData?.reviewer?.avatar || {}),
            },
          },
          reporting: {
            ...defaultAdminProfile.reporting,
            ...(userData?.reporting || {}),
          },
        });
        setIsLoading(true);
      })
      .catch((error) => {
        setIsLoading(true);
        //TODO: Address this popup error
        // activateNotification({
        //   title: 'Error',
        //   content: error.response.data.message,
        //   kind: 'error',
        // });
      });
  }, [auth?.tokens.access.token, useParamsId]);

  let reviewerAvatar =
    profile.reviewer && profile.reviewer.avatar
      ? profile.reviewer.avatar.url
      : '';

  const [progress, setProgress] = useState(false);
  const uploadAvatar = async (e: any) => {
    e.preventDefault();
    let avatarData = {
      avatar: base64Upload,
    };
    setProgress(true);
    await fetch(`${BaseURL}/users/${useParamsId}/upload-avatar`, {
      mode: 'cors',
      method: 'POST',
      headers: {
        'content-Type': 'application/json',
        Authorization: `Bearer ${auth?.tokens.access.token}`,
      },
      body: JSON.stringify(avatarData),
    })
      .then((res) => {
        if (res.ok) {
          activateNotification({
            title: 'Success',
            content: 'Avatar uploaded succesfully',
            kind: 'success',
          });
        } else {
          activateNotification({
            title: 'Error',
            content: res.statusText,
            kind: 'error',
          });
        }
        return res.json();
      })
      .then(() => {
        navigate(`/edit-profile/${useParamsId}?page=${currentPage}`);
      });
  };

  //

  const request = new AdminSettingsRequestImpl();
  const controller = new AdminSettingsController(request);
  const {
    setUsersGroups,
    usersGroups,
    users: allUsers,
    getUsers,
    setCurrentUserGroups,
  } = usersStore;

  const uiUsersFormat = useMemo(() => {
    return allUsers.map((user) => {
      return {
        label: {
          avatar: {
            name:
              user.firstName && user.lastName
                ? `${user.firstName} ${user.lastName}`
                : user.email,
            src: user.avatar && user.avatar.url ? user.avatar.url : '',
          },
          id: user.id,
          name:
            user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.email,
        },
        value: user.id,
      };
    });
  }, [allUsers]);
  const navigate = useNavigate();
  const {id} = useParams();

  const submitEmail = async (data: AdminEmailData) => {
    await controller.newAdminEmail(data, id);
    setInitResponseAsAuth();

    navigate(membersPageRoute);
    // activateDashboardTab('admin');
  };

  const submitReviewer = async (data: AdminReviewerData) => {
    await controller.newAdminReviewer(data, id);

    getUsers();

    setInitResponseAsAuth();

    // activateDashboardTab('admin');
  };

  const {
    firstName,
    lastName,
    phoneNumber,
    jobTitle,
    timezone,
    groups,
    countryCode,
  } = watch();

  const [isDisabledEmail, setIsDisabledEmail] = useState(true);
  const {email} = watchEmail();

  const [isDisabledReviewer] = useState(true);
  const {reviewer} = watchReviewer();

  const [image, setImage] = useState({preview: '', raw: '', disable: false});

  const [base64Upload, setbase64Upload] = useState<any | null>(null);

  const handleChange = async (e: any) => {
    const file = e.target.files[0];
    const base64 = await resizeFile(file);
    setbase64Upload(base64);
    if (e.target.files.length) {
      setImage({
        preview: URL.createObjectURL(e.target.files[0]),
        raw: e.target.files[0],
        disable: true,
      });
    }
  };

  const isDisabled = useMemo(() => {
    if (profile.id === '') {
      return true;
    }

    const currentUserGroups = usersStore.usersGroups[profile.id] || [];
    const currentGroupIds = currentUserGroups
      .map((group: any) => group.id)
      .sort();
    const selectedGroupIds = (groups || currentUserGroups)
      .map((group: any) => group.value || group.id)
      .sort();

    const normalizedFirstName =
      firstName !== undefined ? firstName : profile.firstName;
    const normalizedLastName =
      lastName !== undefined ? lastName : profile.lastName;
    const normalizedPhoneNumber =
      phoneNumber !== undefined ? phoneNumber : profile.phoneNumber;
    const normalizedJobTitle =
      jobTitle !== undefined ? jobTitle : profile.jobTitle;
    const normalizedTimezone = normalizeTimezoneIdentifier(
      timezone !== undefined ? timezone : profile.timezone,
    );
    const normalizedCountryCode =
      countryCode !== undefined ? countryCode : profile.countryCode;
    const normalizedReviewer =
      reviewer !== undefined ? reviewer : profile.reviewer?.id || '';

    const hasChanges =
      normalizedFirstName !== profile.firstName ||
      normalizedLastName !== profile.lastName ||
      normalizedPhoneNumber !== profile.phoneNumber ||
      normalizedJobTitle !== profile.jobTitle ||
      normalizedTimezone !== normalizeTimezoneIdentifier(profile.timezone) ||
      normalizedCountryCode !== profile.countryCode ||
      normalizedReviewer !== (profile.reviewer?.id || '') ||
      JSON.stringify(selectedGroupIds) !== JSON.stringify(currentGroupIds);

    return !hasChanges;
  }, [
    countryCode,
    firstName,
    groups,
    jobTitle,
    lastName,
    phoneNumber,
    profile.countryCode,
    profile.firstName,
    profile.id,
    profile.jobTitle,
    profile.lastName,
    profile.phoneNumber,
    profile.reviewer?.id,
    profile.timezone,
    reviewer,
    timezone,
    usersStore.usersGroups,
  ]);

  useMemo(() => {
    email ? setIsDisabledEmail(false) : setIsDisabledEmail(true);
  }, [email]);

  // Initialize groups
  const fetchGroups = useCallback(async () => {
    const request = new AnalyticsRequestImpl();
    const controller = new AnalyticsController(request);
    const groupsResponse = await controller.fetchGroups();
    setFilterGroup(
      groupsResponse &&
        // eslint-disable-next-line array-callback-return
        groupsResponse.results.map((group: any) => {
          if (group) {
            return {
              label:
                group.name === 'General'
                  ? `General (${group.groupType?.name})`
                  : group.name,
              value: group.id,
            };
          }
        }),
    );
  }, [setFilterGroup]);
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);
  const {id: currentUserId} = useParams();
  const fetchCurrentUserGroups = useCallback(async () => {
    if (!currentUserId) {
      return;
    }

    const request = new UserAPIRequestImpl();
    const controller = new UserAPIController(request);
    const response: any = await controller.fetchSpecificUserGroups(
      currentUserId,
    );

    response && setUsersGroups(currentUserId, response);
    response && setCurrentUserGroups(response);
  }, [currentUserId, setCurrentUserGroups, setUsersGroups]);
  useEffect(() => {
    fetchCurrentUserGroups();
  }, [fetchCurrentUserGroups]);

  const [updateMemberShip, setUpdateMembership] = useState(false);

  const submitForm = async (data: AdminSettingsFormData) => {
    const groupsRequest = new groupRequestImpl();
    const groupsController = new groupController(groupsRequest);
    const currentUserGroups = usersGroups[currentUserId] || [];

    const {groups, ...profileData} = data;
    if (profileData.timezone) {
      profileData.timezone = serializeTimezoneForApi(profileData.timezone);
    }

    // if (reviewer) {
    //   submitReviewer({
    //     reviewer: watchReviewer().reviewer,
    //     reporting: watchReviewer().reporting,
    //   });
    // }
    if (groups && currentUserId) {
      const defaultData = currentUserGroups.map((grp: any) => grp.id);
      const selectedData = groups.map((grp: any) => grp.value);
      const toBeRemoved = defaultData.filter(
        (grp: any) => !selectedData.includes(grp),
      );
      const toBeAdded = selectedData.filter(
        (grp: any) => !defaultData.includes(grp),
      );

      if (toBeAdded.length > 0) {
        const addedData = groups.map((grp: any) =>
          toBeAdded.includes(grp.value),
        );
        setUsersGroups(currentUserId, [
          ...currentUserGroups,
          ...addedData,
        ]);
        const responseAdd = await Promise.all(
          toBeAdded.map(async (group: any) => {
            const dt = {add: {users: [id], type: 'add'}};
            await groupsController.newAddGroupMember(dt, group);
          }),
        );

        responseAdd && setUpdateMembership(true);
      }
      if (toBeRemoved.length > 0) {
        setUsersGroups(
          currentUserId,
          currentUserGroups.filter(
            (grp: any) => !toBeRemoved.includes(grp.id),
          ),
        );
        const responseRemove = await Promise.all(
          toBeRemoved.map(async (group: any) => {
            const dt = {remove: {users: [id]}};
            await groupsController.newAddGroupMember(dt, group);
          }),
        );
        responseRemove && setUpdateMembership(true);
      }
    }
    updateMemberShip &&
      activateNotification({
        title: 'Success',
        content: 'Successfully updated group members',
        kind: 'success',
      });

    await controller.newAdminSettingsForm({...profileData, reviewer}, id);
    setInitResponseAsAuth();
    setFirebaseData(`updates/user`, generateRandomDigits(16));

    fetchCurrentUserGroups();
    navigate(membersPageRoute);
    // activateDashboardTab('admin');
  };

  return {
    register,
    errors,
    isSubmitting,
    isSubmittingEmail,
    isSubmittingReviewer,
    submitForm,
    submitEmail,
    submitReviewer,
    handleSelectChange,
    handleSelectChangeGroups,
    handleSelectChangeReviewer,
    handleSelectChangeWeekday,
    handleSelectChangeSchedule,
    handleSubmit,
    control,
    emailControl,
    reviewerControl,
    image,
    base64Upload,
    handleChange,
    hasError,
    isDisabled,
    isDisabledEmail,
    isDisabledReviewer,
    emailRegister,
    reviewerRegister,
    emailErrors,
    reviewerErrors,
    emailHandleSubmit,
    reviewerHandleSubmit,
    users: uiUsersFormat.map((user: any) => {
      if (
        user &&
        user.label &&
        user.label.name &&
        user.label.name.includes('@')
      ) {
        return {
          ...user,
          label: {
            ...user.label,
            name: `${user.label.name} (pending invitation)`,
          },
        };
      } else {
        return user;
      }
    }),
    profile,
    profileAvatar,
    isLoading,
    reviewerAvatar,
    progress,
    uploadAvatar,
    handleSetPrefix,
    membersPageRoute,
  };
}
